import React, { useState, useEffect } from "react";
import { createUser } from "./api/users";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

interface Role {
  role_id: number;
  role_desc: string;
}

interface CreateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserCreated: () => void;
  roles: Role[];
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ 
  open, 
  onOpenChange, 
  onUserCreated, 
  roles 
}) => {
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Form state
  const [formData, setFormData] = useState({
    ecode: "",
    name: "",
    mobile_no: "",
    email: "",
    role_id: ""
  });

  // Reset form when opened
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setCreateError("");
    setValidationErrors({});
    setFormData({
      ecode: "",
      name: "",
      mobile_no: "",
      email: "",
      role_id: ""
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }

    // Real-time validation
    if (field === "mobile_no" && value) {
      const mobileRegex = /^\d{10}$/;
      if (value.length > 0 && !mobileRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          mobile_no: value.length !== 10 ? "Must be exactly 10 digits" : "Only numbers allowed"
        }));
      }
    }

    if (field === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value.length > 0 && !emailRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          email: "Please enter a valid email address"
        }));
      }
    }
  };


  const handleCreateUser = async () => {
    try {
      setCreating(true);
      setCreateError("");

      // Validation
      if (!formData.ecode.trim()) {
        setCreateError("Employee code is required");
        return;
      }

      if (!formData.name.trim()) {
        setCreateError("Name is required");
        return;
      }

      // Mobile number validation (10 digits)
      if (!formData.mobile_no.trim()) {
        setCreateError("Mobile number is required");
        return;
      }
      const mobileRegex = /^\d{10}$/;
      if (!mobileRegex.test(formData.mobile_no.trim())) {
        setCreateError("Mobile number must be exactly 10 digits");
        return;
      }

      // Email validation
      if (!formData.email.trim()) {
        setCreateError("Email is required");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setCreateError("Please enter a valid email address");
        return;
      }

      if (!formData.role_id) {
        setCreateError("Role is required");
        return;
      }

      const userData = {
        ecode: formData.ecode.trim(),
        name: formData.name.trim(),
        mobile_no: formData.mobile_no.trim(),
        email: formData.email.trim(),
        role_id: parseInt(formData.role_id)
      };

      await createUser(userData);

      // Notify parent component to refresh users list
      if (onUserCreated) {
        onUserCreated();
      }

      // Close modal
      onOpenChange(false);
    } catch (err: any) {
      console.error("Error creating user:", err);

      // Extract error message from backend response
      let errorMessage = "Failed to create user";
      
      if (err?.response?.data?.detail) {
        // Handle string detail message
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
        // Handle array of validation errors
        else if (Array.isArray(err.response.data.detail) && err.response.data.detail.length > 0) {
          const firstError = err.response.data.detail[0];
          errorMessage = firstError.msg || "Validation error";
        }
      } else if (err?.response?.status === 400 && err?.response?.data) {
        // Handle other 400 error formats
        errorMessage = err.response.data.detail || err.response.data.message || "Bad request";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setCreateError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-medium">Create New User</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {createError && (
            <Alert variant="destructive">
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}

          {/* Employee Code */}
          <div className="space-y-2">
            <Label htmlFor="ecode" className="text-sm font-medium">Employee Code *</Label>
            <Input
              id="ecode"
              value={formData.ecode}
              onChange={(e) => handleInputChange('ecode', e.target.value)}
              placeholder="Enter employee code "
              autoFocus={false}
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter full name"
              autoFocus={false}
            />
          </div>

          {/* Mobile Number */}
          <div className="space-y-2">
            <Label htmlFor="mobile" className="text-sm font-medium">Mobile Number *</Label>
            <Input
              id="mobile"
              value={formData.mobile_no}
              onChange={(e) => handleInputChange('mobile_no', e.target.value)}
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
              className={validationErrors.mobile_no ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.mobile_no && (
              <p className="text-sm text-red-600">{validationErrors.mobile_no}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter email address"
              className={validationErrors.email ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.email && (
              <p className="text-sm text-red-600">{validationErrors.email}</p>
            )}
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role" className="text-sm font-medium">Role *</Label>
            <Select value={formData.role_id} onValueChange={(value) => handleInputChange('role_id', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.role_id} value={role.role_id.toString()}>
                    {role.role_desc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateUser} disabled={creating}>
            {creating ? 'Creating...' : 'Create User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;
